import {
  Component,
  ElementRef,
  ViewChild,
  inject,
  signal,
  output,
  computed,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';

import { NzInputModule } from 'ng-zorro-antd/input';
import { NzUploadModule, NzUploadFile } from 'ng-zorro-antd/upload';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzMentionModule, NzMentionComponent } from 'ng-zorro-antd/mention';

import { NotificationService, VoiceRecordingService } from '@rapider/services';
import { AuthStore } from '@rapider/authentication';
import { ProjectDataService } from '@rapider/project-data';
import { selectDataSources, DataSourceActions } from '@rapider/data-source';
import {
  selectSelectedAiFunctionId,
  selectAvailableAiFunctions,
} from '../state/ai-chat.reducer';
import { AiChatActions } from '../state/ai-chat.actions';
import {
  AI_CHAT_CONFIG,
  FileAttachment,
  formatRecordingDuration,
  validateFileAttachment,
  formatFileSize,
  DEFAULT_AGENT_NAME,
} from '../ai-chat.config';

@Component({
  selector: 'rapider-chat-input',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzInputModule,
    NzUploadModule,
    NzButtonModule,
    NzTooltipModule,
    NzDropdownModule,
    NzMenuModule,
    NzMentionModule,
  ],
  templateUrl: './chat-input.component.html',
  styles: [
    `
      ::ng-deep .custom-nz-mention.ant-mentions,
      ::ng-deep .custom-nz-mention .ant-mentions {
        border: none !important;
        border-width: 0 !important;
        border-color: transparent !important;
        box-shadow: none !important;
        background: transparent !important;
        outline: none !important;
      }
      ::ng-deep .custom-nz-mention.ant-mentions.ant-mentions-focused,
      ::ng-deep .custom-nz-mention .ant-mentions.ant-mentions-focused,
      ::ng-deep .custom-nz-mention textarea:focus {
        border: none !important;
        border-width: 0 !important;
        border-color: transparent !important;
        box-shadow: none !important;
        outline: none !important;
      }
      ::ng-deep .custom-nz-mention textarea.ant-input,
      ::ng-deep .custom-nz-mention .ant-input {
        border: none !important;
        border-width: 0 !important;
        border-color: transparent !important;
        box-shadow: none !important;
        background: transparent !important;
        outline: none !important;
      }
    `,
  ],
})
export class ChatInputComponent {
  private readonly notificationService = inject(NotificationService);
  private readonly voiceService = inject(VoiceRecordingService);
  private readonly store = inject(Store);
  private readonly authStore = inject(AuthStore);
  private readonly projectDataService = inject(ProjectDataService);

  public readonly config = AI_CHAT_CONFIG;

  // Expose signals for HTML mapping
  public isRecording = this.voiceService.isRecording;

  // AI Function Context State
  public selectedAiFunctionId = this.store.selectSignal(
    selectSelectedAiFunctionId,
  );
  public availableAiFunctions = this.store.selectSignal(
    selectAvailableAiFunctions,
  );

  public selectedAiFunctionName = computed(() => {
    const id = this.selectedAiFunctionId();
    const functions = this.availableAiFunctions();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const agent = functions.find((a: any) => a.id === id || a.name === id);
    return agent ? agent.title || agent.name : DEFAULT_AGENT_NAME;
  });

  // Output events to parent smart component
  public onSendMessage = output<{
    text: string;
    attachments: FileAttachment[];
  }>();
  public onSendVoice = output<{ file: File }>();

  // Local State
  public messageText = signal<string>('');
  @ViewChild('messageTextarea')
  messageTextarea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild(NzMentionComponent) mentionComponent!: NzMentionComponent;
  public pendingAttachments = signal<FileAttachment[]>([]);

  public formatSize = formatFileSize;

  // Mention State
  private dataSources = this.store.selectSignal(selectDataSources);
  private projectApplications = signal<any[]>([]);

  public mentionSuggestions = computed(() => {
    const dbs = (this.dataSources() || [])
      .filter((ds) => ds.connector !== 'mongodb')
      .map((ds) => ({
        name: ds.name,
        label: ds.title || ds.name,
        icon: this.getConnectorIcon(ds.connector),
      }));

    const apps = (this.projectApplications() || []).map((app) => ({
      name: app.name,
      label: app.title || app.name,
      icon: 'fas fa-robot', // Default application icon
    }));

    return [...dbs, ...apps];
  });

  constructor() {
    effect(() => {
      const project = this.authStore.activeProject();
      const projectId = project?.id;
      if (projectId) {
        this.projectDataService
          .getProjectApplications(projectId)
          .subscribe((apps) => {
            this.projectApplications.set(apps || []);
          });
        // Ensure data sources are loaded for mentions even if not visited yet
        this.store.dispatch(DataSourceActions.loadDataSources());
      } else {
        this.projectApplications.set([]);
      }
    });
  }

  public mentionValueWith = (item: any) => item.name;

  public onMentionSearchChange(mention: any): void {
    if (!this.dataSources() || this.dataSources().length === 0) {
      this.store.dispatch(DataSourceActions.loadDataSources());
    }
  }

  private getConnectorIcon(id?: string): string {
    const connectors: Record<string, string> = {
      'file-storage': 'fas fa-folder-open',
      mongodb: 'fas fa-database',
      postgres: 'fas fa-database',
      mysql: 'fas fa-database',
      mssql: 'fas fa-database',
      'rest-api': 'fas fa-plug',
    };
    return id ? connectors[id] || 'fas fa-database' : 'fas fa-database';
  }

  /** Resolves current duration from the voice service and maps it to a string. */
  public getFormattedDuration(): string {
    return formatRecordingDuration(this.voiceService.recordingDuration());
  }

  /** Captures Enter key to send message (Shift+Enter for newline). */
  public handleEnterKey(event: any): void {
    if (!event.shiftKey) {
      if (this.mentionComponent && this.mentionComponent.isOpen) {
        // Allow the mention component to handle the Enter key to select the item
        return;
      }
      event.preventDefault();
      this.dispatchMessage();
    }
  }

  /** Intercepts NG Zorro file uploads natively. */
  public beforeUpload = (file: NzUploadFile): boolean => {
    const physicalFile =
      (file as any).originFileObj || (file as unknown as File);

    if (this.pendingAttachments().length >= 5) {
      this.notificationService.createNotification(
        'warning',
        'Limit Reached',
        this.config.texts.maxFilesReached,
      );
      return false;
    }

    const validation = validateFileAttachment(physicalFile);
    if (!validation.isValid) {
      this.notificationService.createNotification(
        'error',
        'Error',
        validation.error!,
      );
      return false;
    }

    const attachment: FileAttachment = {
      id: Math.random().toString(36).substring(2, 11),
      name: physicalFile.name,
      size: physicalFile.size,
      mimeType: physicalFile.type,
      file: physicalFile,
    };

    this.pendingAttachments.update((prev) => [...prev, attachment]);
    return false; // Prevent auto-upload by component
  };

  public removeAttachment(id: string): void {
    this.pendingAttachments.update((prev) => prev.filter((a) => a.id !== id));
  }

  public selectAiFunction(id: string): void {
    this.store.dispatch(AiChatActions.selectAiFunction({ aiFunctionId: id }));
  }

  public dispatchMessage(): void {
    const text = this.messageText().trim();
    const attachments = this.pendingAttachments();

    if (text.length === 0 && attachments.length === 0) return;

    this.onSendMessage.emit({ text, attachments });
    this.messageText.set('');
    this.pendingAttachments.set([]);
  }

  // --- Voice Controls ---

  public async startVoiceRecording(): Promise<void> {
    try {
      await this.voiceService.startRecording();
    } catch (error) {
      this.notificationService.createNotification(
        'error',
        'Error',
        this.config.texts.micPermissionDenied,
      );
    }
  }

  public async stopAndSendVoiceRecording(): Promise<void> {
    const audioFile = await this.voiceService.stopRecording();
    if (audioFile) {
      this.onSendVoice.emit({ file: audioFile });
    }
  }

  public cancelVoiceRecording(): void {
    this.voiceService.cancelRecording();
  }
}
